import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  useBreakpointValue,
  Stack,
  Heading,
  Text,
  Slide,
  SlideFade,
} from '@chakra-ui/react';
// And react-slick as our Carousel Lib

import { ArrowLeftIcon, ArrowRightIcon } from '@chakra-ui/icons';
import { Breakpoint, breakpoints } from '../../utils/breakpoints';
import clsx from 'clsx';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  PanInfo,
  animate,
  AnimationOptions,
} from 'framer-motion';
import { DynamicImage } from '../Image/Image';
import { ClassNames } from '@emotion/react';

const transition: AnimationOptions<any> = {
  type: 'spring',
  bounce: 0,
};

const Container = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; className: string }
>((props, ref) => (
  <div
    className={clsx(
      'relative flex h-full w-full items-center overflow-x-hidden',
      props.className
    )}
    ref={ref}
  >
    {props.children}
  </div>
));

export type CarouselRef = {
  prev: () => void;
  next: () => void;
  setIndex: (index: number) => void;
};

/**
 * @param  {number} autoPlay 0 Means that it wont autoPlay, everything over 0 will autoplay in sec. Default 0
 */
export const Carousel = React.forwardRef(
  (
    { children, autoPlay = 0 }: React.PropsWithChildren<{ autoPlay?: number }>,
    ref: React.Ref<CarouselRef>
  ) => {
    // As we have used custom buttons, we need a reference variable to
    // change the state
    const [index, setIndex] = useState<number>(0);
    const x = useMotionValue(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const calcNextX = () => -index * (containerRef.current?.clientWidth || 0);

    const handleDrag = (e: Event, drag: PanInfo) => {
      const clientWidth = containerRef.current?.clientWidth || 0;

      const { offset } = drag;
      if (offset.x > clientWidth / 4) {
        prev();
      } else if (offset.x < -clientWidth / 4) {
        next();
      } else {
        animate(x, calcNextX(), transition);
      }
    };

    const next = () => {
      setIndex((c) => (c + 1 === React.Children.count(children) ? 0 : c + 1));
    };

    const prev = () => {
      setIndex((c) => (c - 1 < 0 ? React.Children.count(children) - 1 : c - 1));
    };

    useEffect(() => {
      if (autoPlay === 0) {
        return;
      }

      const timer = setInterval(next, autoPlay * 1000);
      return () => clearInterval(timer);
    }, [next, autoPlay]);

    useEffect(() => {
      const controls = animate(x, calcNextX(), transition);
      return controls.stop;
    }, [index]);
    // These are the breakpoints which changes the position of the
    // buttons as the screen size changes
    const top = useBreakpointValue({ base: '90%', md: '50%' });
    const side = useBreakpointValue({ base: '30%', md: '40px' });

    React.useImperativeHandle(
      ref,
      () => {
        return {
          next,
          prev,
          setIndex,
        };
      },
      [index]
    );
    // This list contains all the data for carousels
    // This can be static or loaded from a server

    return (
      <Box className="group" position={'relative'} overflow={'hidden'}>
        {/* Left Icon */}
        <label className="absolute top-0 left-4 bottom-0 z-20 m-auto flex items-center justify-start lg:h-full lg:w-1/3">
          <IconButton
            className="group-hover:text-default-900 group-hover:bg-white"
            aria-label="Vorheriges Bild"
            variant="ghost"
            onClick={prev}
          >
            <ArrowLeftIcon />
          </IconButton>
        </label>

        {/* Right Icon */}
        <label className="absolute top-0 right-4 bottom-0 z-20 m-auto flex items-center justify-end bg-opacity-40 xl:h-full  xl:w-1/3">
          <IconButton
            className="group-hover:text-default-900 group-hover:bg-white"
            aria-label="Nächstest Bild"
            variant="ghost"
            onClick={next}
          >
            <ArrowRightIcon />
          </IconButton>
        </label>

        {/* Slider */}
        <Container ref={containerRef} className="z-10">
          {React.Children.map(children, (child, i) => (
            <motion.div
              drag={React.Children.count(children) > 1 && 'x'}
              dragElastic={0.3}
              style={{
                width: '100%',
                height: 'auto',
                display: 'inline-block',
                flex: 'none',
                x,
                left: `${i * 100}%`,
                right: `${i * 100}%`,
              }}
              onDragEnd={handleDrag}
              key={i}
            >
              <div>{child}</div>
            </motion.div>
          ))}
        </Container>
      </Box>
    );
  }
);
